import classNames from "classnames";
import React from "react";

import { Select } from "./../Form";
import { IStyleSize } from "./../Style";
import { Link } from "./Link";
import { PageItem } from "./PageItem";
import { Separator } from "./Separator";

interface IPageableMeta {
  first: boolean;
  last: boolean;
  totalElements: number;
  numberOfElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface IPaginationOption {
  value: number;
  label: string;
}

export interface IPaginationProps extends Partial<IStyleSize> {
  className?: string;
  config: IPageableMeta;
  paginationStep?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (page: number) => void;
  sizePreElement?: React.ReactNode;
  sizePostElement?: React.ReactNode;
  pagePreviousLabel?: string;
  pageNextLabel?: string;
  paginationOptions?: IPaginationOption[];
}

const PAGINATION__STEP = 2;
const PAGE_SIZE_OPTIONS: IPaginationOption[] = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
];

export class Pagination extends React.PureComponent<IPaginationProps> {
  static defaultProps: Partial<IPaginationProps> = {
    size: "md",
    paginationStep: PAGINATION__STEP,
    sizePreElement: <span>Show</span>,
    sizePostElement: <span>Items</span>,
    pagePreviousLabel: "Prev",
    pageNextLabel: "Next",
    paginationOptions: PAGE_SIZE_OPTIONS,
  };

  prevPage = (event: React.MouseEvent<any>) => {
    const page = this.props.config.number - 1;
    this.changePage(page, event);
  };

  nextPage = (event: React.MouseEvent<any>) => {
    const page = this.props.config.number + 1;
    this.changePage(page, event);
  };

  changePage = (page: number, event: React.MouseEvent<any>) => {
    event.preventDefault();
    if (page >= 0 && page < this.props.config.totalPages) {
      const { onPageChange } = this.props;
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  changePageSize = (size: IPaginationOption) => {
    const { onPageSizeChange } = this.props;
    if (onPageSizeChange) {
      onPageSizeChange(size.value);
    }
  };

  createPage = ({ index, label, active }: { index: number; label: number; active: boolean }) => {
    return <PageItem key={index} label={label} active={active} onChange={this.changePage.bind(this, index)} />;
  };

  createPages = (from: number, to: number) => {
    const elements = [];
    for (let i = from; i < to; i++) {
      const pageConfig = { index: i, label: i + 1, active: i === this.props.config.number };
      elements.push(this.createPage(pageConfig));
    }
    return elements;
  };

  createFirstWithSeparator = () => {
    const pageConfig = { index: 0, label: 1, active: 0 === this.props.config.number };
    const firstPage = this.createPage(pageConfig);
    return [firstPage, <Separator key={1} />];
  };

  createLastWithSeparator = () => {
    const totalPages = this.props.config.totalPages;
    const pageConfig = {
      index: totalPages - 1,
      label: totalPages,
      active: totalPages - 1 === this.props.config.number,
    };
    const lastPage = this.createPage(pageConfig);
    return [<Separator key={totalPages - 2} />, lastPage];
  };

  renderPrev = () => {
    return <Link label={this.props.pagePreviousLabel} onClick={this.prevPage} />;
  };

  renderNext = () => {
    return <Link label={this.props.pageNextLabel} onClick={this.nextPage} />;
  };

  renderPagesList = ({ totalPages }: IPageableMeta) => {
    const elements = [] as any;
    const activePage = this.props.config.number;

    const paginationStep = this.props.paginationStep as number;

    if (totalPages < paginationStep * 2 + 6) {
      elements.push(...this.createPages(0, totalPages));
    } else if (activePage < paginationStep * 2 + 1) {
      elements.push(...this.createPages(0, paginationStep * 2 + 4 - 1));
      elements.push(...this.createLastWithSeparator());
    } else if (activePage > totalPages - 1 - paginationStep * 2) {
      elements.push(...this.createFirstWithSeparator());
      elements.push(...this.createPages(totalPages - 1 - paginationStep * 2 - 2, totalPages));
    } else {
      elements.push(...this.createFirstWithSeparator());
      elements.push(...this.createPages(activePage - paginationStep, activePage + paginationStep + 1));
      elements.push(...this.createLastWithSeparator());
    }

    return elements;
  };

  getPaginationValue = (size: number) => this.props.paginationOptions?.find(o => o.value === size);

  render() {
    const {
      className,
      size,
      config = {} as IPageableMeta,
      sizePreElement: sizePreLabel,
      sizePostElement: sizePostLabel,
    } = this.props;

    const baseClass = "el-pagination";
    const classes = classNames(
      baseClass,
      {
        [`${baseClass}--size-${size}`]: !!size,
      },
      className,
    );

    const { first, last } = config;

    return (
      <div className={classes}>
        <div className="el-pagination-pages">
          {!first && <div className="el-pagination-link--prev">{this.renderPrev()}</div>}
          <ul className="el-pagination-list">{this.renderPagesList(config)}</ul>
          {!last && <div className="el-pagination-link--next">{this.renderNext()}</div>}
        </div>
        {this.props.onPageSizeChange && (
          <div className="el-pagination-size">
            {sizePreLabel}
            <Select
              className="el-pagination-size-select"
              isClearable={false}
              name="select"
              labelKey="label"
              valueKey="value"
              options={this.props.paginationOptions}
              onChange={this.changePageSize}
              size={size}
              value={this.getPaginationValue(config.size)}
            />
            {sizePostLabel}
          </div>
        )}
      </div>
    );
  }
}

export default Pagination;
